import React, { useState, useEffect } from 'react';
import { TransactionFilter, TransactionStatus, RiskLevel } from '../../types/api';
import { ethers } from 'ethers';

interface TransactionFiltersProps {
  filter: TransactionFilter;
  onFilterChange: (filter: TransactionFilter) => void;
}

const TransactionFilters: React.FC<TransactionFiltersProps> = ({ filter, onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
  const [minAmountInput, setMinAmountInput] = useState<string>('');
  const [maxAmountInput, setMaxAmountInput] = useState<string>('');

  useEffect(() => {
    setMinAmountInput(filter.minAmount ? ethers.formatEther(filter.minAmount) : '');
    setMaxAmountInput(filter.maxAmount ? ethers.formatEther(filter.maxAmount) : '');
  }, [filter.minAmount, filter.maxAmount]);


  const handleStatusChange = (status: TransactionStatus, checked: boolean) => {
    const currentStatuses = filter.status || [];
    const newStatuses = checked 
      ? [...currentStatuses, status]
      : currentStatuses.filter(s => s !== status);
    
    onFilterChange({
      ...filter,
      status: newStatuses.length > 0 ? newStatuses : undefined
    });
  };

  const handleRiskChange = (risk: RiskLevel, checked: boolean) => {
    const currentRisks = filter.riskLevel || [];
    const newRisks = checked 
      ? [...currentRisks, risk]
      : currentRisks.filter(r => r !== risk);
    
    onFilterChange({
      ...filter,
      riskLevel: newRisks.length > 0 ? newRisks : undefined
    });
  };

  const handleChainChange = (chainId: number, checked: boolean) => {
    const currentChains = filter.chains || [];
    const newChains = checked 
      ? [...currentChains, chainId]
      : currentChains.filter(c => c !== chainId);
    
    onFilterChange({
      ...filter,
      chains: newChains.length > 0 ? newChains : undefined
    });
  };

  const handleAmountBlur = (field: 'minAmount' | 'maxAmount', value: string) => {
    try {
      onFilterChange({
        ...filter,
        [field]: value ? ethers.parseEther(value).toString() : undefined
      });
    } catch (error) {
      console.error("Invalid amount format:", error);
      // Optionally, provide user feedback about the invalid format
    }
  };

  const handleQuickFilter = (quickFilter: string) => {
    setActiveQuickFilter(quickFilter);
    let newFilter: TransactionFilter = { ...filter };

    switch (quickFilter) {
      case '24h':
        newFilter.dateRange = {
          start: new Date(Date.now() - 24 * 60 * 60 * 1000),
          end: new Date()
        };
        break;
      case '7d':
        newFilter.dateRange = {
          start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          end: new Date()
        };
        break;
      case '30d':
        newFilter.dateRange = {
          start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          end: new Date()
        };
        break;
      case 'needs_attention':
        newFilter.status = [TransactionStatus.PENDING, TransactionStatus.TIMEOUT];
        break;
      case 'high_risk':
        newFilter.riskLevel = [RiskLevel.HIGH, RiskLevel.CRITICAL];
        break;
    }
    onFilterChange(newFilter);
  };

  const clearFilters = () => {
    setActiveQuickFilter(null);
    onFilterChange({});
  };

  const hasActiveFilters = Object.keys(filter).some(key => {
    const value = filter[key as keyof TransactionFilter];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null;
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            {isExpanded ? 'Hide filters' : 'Show filters'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="space-y-2">
              {Object.values(TransactionStatus).map((status) => (
                <label key={status} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filter.status?.includes(status) || false}
                    onChange={(e) => handleStatusChange(status, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 capitalize">
                    {status.replace('_', ' ')}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Risk Level Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
            <div className="space-y-2">
              {Object.values(RiskLevel).map((risk) => (
                <label key={risk} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filter.riskLevel?.includes(risk) || false}
                    onChange={(e) => handleRiskChange(risk, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 capitalize">
                    {risk}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Chain Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Chains</label>
            <div className="space-y-2">
              {[
                { id: 56, name: 'BSC Mainnet' },
                { id: 97, name: 'BSC Testnet' },
                { id: 2020, name: 'Ronin Mainnet' },
                { id: 2021, name: 'Ronin Testnet' }
              ].map((chain) => (
                <label key={chain.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filter.chains?.includes(chain.id) || false}
                    onChange={(e) => handleChainChange(chain.id, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {chain.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Amount Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount Range</label>
            <div className="space-y-2">
              <input
                type="number"
                placeholder="Min amount"
                value={minAmountInput}
                onChange={(e) => setMinAmountInput(e.target.value)}
                onBlur={(e) => handleAmountBlur('minAmount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <input
                type="number"
                placeholder="Max amount"
                value={maxAmountInput}
                onChange={(e) => setMaxAmountInput(e.target.value)}
                onBlur={(e) => handleAmountBlur('maxAmount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick Date Range Filters */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Quick filters:</span>
          <button
            onClick={() => handleQuickFilter('24h')}
            className={`px-3 py-1 text-xs rounded-full ${
              activeQuickFilter === '24h' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Last 24h
          </button>
          <button
            onClick={() => handleQuickFilter('7d')}
            className={`px-3 py-1 text-xs rounded-full ${
              activeQuickFilter === '7d' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Last 7 days
          </button>
          <button
            onClick={() => handleQuickFilter('30d')}
            className={`px-3 py-1 text-xs rounded-full ${
              activeQuickFilter === '30d' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Last 30 days
          </button>
          <button
            onClick={() => handleQuickFilter('needs_attention')}
            className={`px-3 py-1 text-xs rounded-full ${
              activeQuickFilter === 'needs_attention' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            Needs attention
          </button>
          <button
            onClick={() => handleQuickFilter('high_risk')}
            className={`px-3 py-1 text-xs rounded-full ${
              activeQuickFilter === 'high_risk' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            High risk
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionFilters; 